import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, AlertTriangle, Building2, Clock, DollarSign, Globe2, MapPin, MessageSquare, Plug, Tag, UserRound, Zap } from "lucide-react";
import { useAppServices } from "../../hook/services";
import { cx } from "../../utils";
import AppIcon from "../../components/ui/AppIcon";
import Card from "../../components/ui/Card";
import Skeleton from "../../components/ui/Skeleton";
import { useToast } from "../../components/ui/Toast";
import useListQuery from "../../hook/useListQuery";
import { planName, priceText } from "../../components/directory/shared";

/**
 * Installs, setup and usage across every app, over a chosen range.
 *
 * Counts follow the same rule as the Installs page — for a sub-account app,
 * only its location rows are installs. Charts are plain SVG: one scale places
 * bars, gridlines and tick labels, and quiet days show as zero.
 */

const RANGES = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
  { days: 365, label: "12 months" },
];

// Palette for SVG fills (Tailwind's own values, so charts match the UI).
const C = {
  brand: "#6366f1",
  violet: "#a78bfa",
  emerald: "#10b981",
  rose: "#f43f5e",
  amber: "#f59e0b",
  grid: "#ececef",
  axis: "#a1a1aa",
};

const fmt = (n) => (n >= 10000 ? `${(n / 1000).toFixed(n >= 100000 ? 0 : 1)}k` : n.toLocaleString());
const dayLabel = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });

// Round tick values: 0 and three or four steps of 1, 2 or 5 × 10ⁿ.
const ticksFor = (max) => {
  if (max <= 0) return [0, 1];
  const raw = max / 4;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 5, 10].map((m) => m * pow).find((s) => s >= raw);
  const out = [];
  for (let v = 0; v <= max + step * 0.001; v += step) out.push(v);
  if (out[out.length - 1] < max) out.push(out[out.length - 1] + step);
  return out;
};

const useWidth = () => {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver(([e]) => setWidth(Math.floor(e.contentRect.width)));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
};

/**
 * Bars over time. `series` stack in order, bottom first. Hover a column for
 * its values.
 */
const TimeChart = ({ data, series, unit, height = 220 }) => {
  const [ref, width] = useWidth();
  const [hover, setHover] = useState(null);

  const pad = { top: 12, right: 8, bottom: 26, left: 40 };
  const innerW = Math.max(0, width - pad.left - pad.right);
  const innerH = height - pad.top - pad.bottom;
  const totals = data.map((d) => series.reduce((s, x) => s + (d[x.key] || 0), 0));
  const ticks = ticksFor(Math.max(0, ...totals));
  const top = ticks[ticks.length - 1] || 1;
  const y = (v) => pad.top + innerH - (v / top) * innerH;
  const band = data.length ? innerW / data.length : 0;
  const barW = Math.max(2, Math.min(28, band * 0.68));
  const every = Math.max(1, Math.ceil(data.length / Math.max(2, Math.floor(innerW / 70))));
  const empty = totals.every((t) => t === 0);

  return (
    <div ref={ref} className="relative w-full" style={{ height }}>
      {width > 0 && (
        <svg width={width} height={height} role="img" aria-label={`Chart of ${series.map((s) => s.label).join(", ")} by ${unit}`}>
          {ticks.map((t) => (
            <g key={t}>
              <line x1={pad.left} x2={width - pad.right} y1={y(t)} y2={y(t)} stroke={C.grid} strokeWidth="1" />
              <text x={pad.left - 8} y={y(t)} dy="0.32em" textAnchor="end" fontSize="10.5" fill={C.axis} style={{ fontVariantNumeric: "tabular-nums" }}>
                {fmt(t)}
              </text>
            </g>
          ))}
          {data.map((d, i) => {
            const cx0 = pad.left + band * i + band / 2;
            let acc = 0;
            return (
              <g key={d.at}>
                {hover === i && <rect x={pad.left + band * i} y={pad.top} width={band} height={innerH} fill="#f4f4f5" />}
                {series.map((s) => {
                  const v = d[s.key] || 0;
                  if (!v) return null;
                  const y1 = y(acc + v);
                  const h = y(acc) - y1;
                  acc += v;
                  return <rect key={s.key} x={cx0 - barW / 2} y={y1} width={barW} height={Math.max(1, h)} rx={barW > 6 ? 2 : 0} fill={s.color} />;
                })}
                {i % every === 0 && (
                  <text x={cx0} y={height - 8} textAnchor="middle" fontSize="10.5" fill={C.axis}>
                    {dayLabel(d.at)}
                  </text>
                )}
                <rect
                  x={pad.left + band * i}
                  y={pad.top}
                  width={band}
                  height={innerH}
                  fill="transparent"
                  onMouseEnter={() => setHover(i)}
                  onMouseLeave={() => setHover(null)}
                />
              </g>
            );
          })}
        </svg>
      )}
      {empty && width > 0 && (
        <p className="pointer-events-none absolute inset-0 grid place-items-center text-[12.5px] text-ink-400">Nothing in this range.</p>
      )}
      {hover !== null && data[hover] && (
        <div
          className="pointer-events-none absolute top-1 z-10 min-w-[9rem] rounded-lg border border-ink-200 bg-white px-3 py-2 text-[12px] shadow-card-elevated"
          style={{ left: Math.min(Math.max(pad.left + band * hover + band / 2 - 72, 0), Math.max(0, width - 150)) }}
        >
          <p className="mb-1 font-semibold text-ink-900">
            {unit === "week" ? `Week of ${dayLabel(data[hover].at)}` : dayLabel(data[hover].at)}
          </p>
          {[...series].reverse().map((s) => (
            <p key={s.key} className="flex items-center gap-2 text-ink-600">
              <span className="h-2 w-2 rounded-sm" style={{ background: s.color }} />
              {s.label}
              <span className="ml-auto font-medium tabular-nums text-ink-900">{(data[hover][s.key] || 0).toLocaleString()}</span>
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

const Legend = ({ series }) => (
  <div className="flex flex-wrap gap-3">
    {series.map((s) => (
      <span key={s.key} className="inline-flex items-center gap-1.5 text-[11.5px] text-ink-500">
        <span className="h-2 w-2 rounded-sm" style={{ background: s.color }} />
        {s.label}
      </span>
    ))}
  </div>
);

const BADGE = {
  brand: "from-brand-500 to-violet-600",
  emerald: "from-emerald-500 to-teal-600",
  violet: "from-violet-500 to-fuchsia-600",
  rose: "from-rose-500 to-orange-500",
};

// A headline figure: coloured badge, big number, one line of context.
const Kpi = ({ icon: Icon, label, value, hint, tone = "brand", alert }) => (
  <div className="relative overflow-hidden rounded-2xl border border-ink-200/70 bg-white p-5 shadow-card">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[12px] font-medium text-ink-500">{label}</p>
        <p className={cx("mt-2 text-[28px] font-bold leading-none tracking-tight tabular-nums", alert ? "text-rose-600" : "text-ink-900")}>{value}</p>
      </div>
      <span className={cx("grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-br text-white shadow-sm", BADGE[tone] || BADGE.brand)}>
        <Icon size={18} aria-hidden />
      </span>
    </div>
    {hint && <p className="mt-3 truncate text-[12px] text-ink-400">{hint}</p>}
  </div>
);

// A secondary figure, small and inline.
const Mini = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 rounded-xl border border-ink-200/70 bg-white px-4 py-3 shadow-card">
    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-ink-100 text-ink-500">
      <Icon size={15} aria-hidden />
    </span>
    <div className="min-w-0">
      <p className="text-[18px] font-semibold leading-none tabular-nums text-ink-900">{value}</p>
      <p className="mt-1 truncate text-[11.5px] text-ink-500">{label}</p>
    </div>
  </div>
);

/** A ranked list with a bar per row, scaled to the largest value. */
const Ranked = ({ rows, empty, valueLabel = (r) => r.n.toLocaleString(), to }) => {
  const max = Math.max(1, ...rows.map((r) => r.n));
  if (!rows.length) return <p className="py-6 text-center text-[12.5px] text-ink-400">{empty}</p>;
  return (
    <ul className="space-y-2.5">
      {rows.map((r) => {
        const name = r.name || <span className="font-mono text-[11.5px] text-ink-400">{r.id}</span>;
        const body = (
          <>
            <div className="flex items-baseline justify-between gap-3 text-[12.5px]">
              <span className="min-w-0 truncate font-medium text-ink-800">{name}</span>
              <span className="shrink-0 tabular-nums text-ink-500">{valueLabel(r)}</span>
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-ink-100">
              <div className="h-full rounded-full bg-brand-500" style={{ width: `${(r.n / max) * 100}%` }} />
            </div>
          </>
        );
        return <li key={r.id || r.name}>{to ? <Link to={to(r)} className="block rounded-md hover:opacity-80">{body}</Link> : body}</li>;
      })}
    </ul>
  );
};

// "$1,234" per currency, joined — plans can be priced in different currencies.
const revenueText = (rows) =>
  rows?.length
    ? rows
        .map((r) => {
          try {
            return new Intl.NumberFormat(undefined, { style: "currency", currency: r.currency, maximumFractionDigits: 0 }).format(r.amount);
          } catch {
            return `${Math.round(r.amount)} ${r.currency}`;
          }
        })
        .join(" + ")
    : "—";

const Analytics = () => {
  const { directory, installs } = useAppServices();
  const toast = useToast();
  const [query, setQuery] = useListQuery({ days: 30, appId: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apps, setApps] = useState([]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams({ days: query.days });
    if (query.appId) params.set("appId", query.appId);
    directory.Analytics(params.toString()).then(({ response, error }) => {
      if (!alive) return;
      if (error) toast(error?.message || error?.error || "Could not load analytics", "error");
      setData(response || null);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.days, query.appId]);

  useEffect(() => {
    installs.Stats().then(({ response }) => setApps(response?.byApp || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const t = data?.totals;
  const rangeLabel = RANGES.find((r) => r.days === query.days)?.label || `${query.days} days`;
  const runs = t ? t.messages + t.actions : 0;
  const errorRate = t && runs + t.failed > 0 ? (t.failed / (runs + t.failed)) * 100 : 0;
  const installSeries = [
    { key: "installs", label: "Installs", color: C.brand },
    { key: "uninstalls", label: "Uninstalls", color: C.rose },
  ];
  const activitySeries = [
    { key: "actions", label: "Actions run", color: C.violet },
    { key: "messages", label: "Messages", color: C.brand },
    { key: "failed", label: "Errors", color: C.rose },
  ];
  const installed = data?.funnel?.[0]?.n || 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-semibold tracking-tight text-ink-900">Analytics</h1>
          <p className="mt-1 text-[13.5px] text-ink-500">Installs, setup and usage {query.appId ? "for one app" : "across every app"} · last {rangeLabel}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-xl border border-ink-200 bg-white p-1 shadow-sm" role="tablist" aria-label="Range">
          {RANGES.map((r) => (
            <button
              key={r.days}
              type="button"
              role="tab"
              aria-selected={query.days === r.days}
              onClick={() => setQuery({ days: r.days })}
              className={cx("h-8 rounded-lg px-3 text-[13px] font-medium transition-colors", query.days === r.days ? "bg-ink-900 text-white" : "text-ink-600 hover:bg-ink-100")}
            >
              {r.label}
            </button>
          ))}
        </div>
        <select
          value={query.appId}
          onChange={(e) => setQuery({ appId: e.target.value })}
          aria-label="App"
          className={cx(
            "h-10 cursor-pointer rounded-xl border px-3 pr-8 text-[13px] shadow-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20",
            query.appId ? "border-brand-300 bg-brand-50/60 text-brand-800" : "border-ink-200 bg-white text-ink-700",
          )}
        >
          <option value="">All apps</option>
          {apps.map((a) => (
            <option key={a.appId} value={a.appId}>
              {a.appName}
            </option>
          ))}
        </select>
        {loading && data && <span className="text-[12px] text-ink-400">Updating…</span>}
        </div>
      </div>

      {!data ? (
        <div className="grid gap-4 lg:grid-cols-3">
          {[0, 1, 2, 3, 4, 5].map((k) => (
            <Skeleton key={k} className="h-40 w-full" rounded="rounded-2xl" />
          ))}
        </div>
      ) : (
        <div className={cx("space-y-5 transition-opacity", loading && "opacity-60")}>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Kpi icon={Plug} tone="brand" label="Active installs" value={t.activeInstalls.toLocaleString()} hint={`${t.newInRange} new in the last ${rangeLabel}`} />
            <Kpi
              icon={DollarSign}
              tone="emerald"
              label="Est. monthly revenue"
              value={revenueText(data.revenue?.monthly)}
              hint={data.revenue?.unpricedInstalls ? `${data.revenue.unpricedInstalls} install${data.revenue.unpricedInstalls === 1 ? "" : "s"} on unpriced plans` : "Active installs on paid plans"}
            />
            <Kpi icon={Zap} tone="violet" label="Messages & actions" value={fmt(t.messages + t.actions)} hint={`${fmt(t.messages)} messages · ${fmt(t.actions)} actions`} />
            <Kpi icon={AlertTriangle} tone="rose" label="Error rate" value={`${errorRate.toFixed(errorRate < 10 ? 1 : 0)}%`} hint={`${t.failed.toLocaleString()} failed in the last ${rangeLabel}`} alert={errorRate >= 5} />
          </div>

          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Mini icon={Building2} label="Companies" value={t.companies.toLocaleString()} />
            <Mini icon={MapPin} label="Locations" value={t.locations.toLocaleString()} />
            <Mini icon={UserRound} label="Installers" value={t.users.toLocaleString()} />
            <Mini icon={MessageSquare} label="On a trial" value={(data.onTrial || 0).toLocaleString()} />
          </div>

          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_22rem] [&>*]:min-w-0">
            <Card title="Installs and uninstalls" subtitle={`Per ${data.unit} · ${rangeLabel}. Uninstalls are dated by the install's last change.`} action={<Legend series={installSeries} />}>
              <TimeChart data={data.timeline} series={installSeries} unit={data.unit} />
            </Card>
            <Card title="Setup funnel" subtitle="Active installs, by how far setup got">
              <ol className="space-y-3">
                {data.funnel.map((f, i) => {
                  const pct = installed ? (f.n / installed) * 100 : 0;
                  return (
                    <li key={f.id}>
                      <div className="flex items-baseline justify-between text-[12.5px]">
                        <span className="font-medium text-ink-800">{f.label}</span>
                        <span className="tabular-nums text-ink-500">
                          {f.n.toLocaleString()} <span className="text-ink-400">· {pct.toFixed(0)}%</span>
                        </span>
                      </div>
                      <div className="mt-1 h-2.5 overflow-hidden rounded-full bg-ink-100">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: [C.brand, "#7c7ff3", C.violet, C.emerald][i] }} />
                      </div>
                    </li>
                  );
                })}
              </ol>
              {installed > 0 && (
                <p className="mt-4 rounded-lg bg-ink-50 px-3 py-2 text-[12px] leading-relaxed text-ink-500">
                  {installed - data.funnel[1].n} active install{installed - data.funnel[1].n === 1 ? " hasn't" : "s haven't"} saved credentials yet.{" "}
                  <Link to="/installs?status=active&credentials=missing" className="font-medium text-brand-600 hover:underline">
                    See them
                  </Link>
                </p>
              )}
            </Card>
          </div>

          <Card title="Usage" subtitle={`Messages, workflow actions and errors per ${data.unit} · ${rangeLabel}`} action={<Legend series={activitySeries} />}>
            <TimeChart data={data.timeline} series={activitySeries} unit={data.unit} height={240} />
          </Card>

          <div className="grid gap-5 lg:grid-cols-2 [&>*]:min-w-0">
            <Card title="By app" subtitle={`Active and uninstalled, with installs new in the last ${rangeLabel}`}>
              {data.byApp.length === 0 ? (
                <p className="py-6 text-center text-[12.5px] text-ink-400">No installs yet.</p>
              ) : (
                <div className="-mx-1 overflow-x-auto px-1">
                <table className="w-full min-w-[22rem] text-[12.5px]">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider text-ink-400">
                      <th className="pb-2 font-semibold">App</th>
                      <th className="pb-2 text-right font-semibold">Active</th>
                      <th className="pb-2 text-right font-semibold">New</th>
                      <th className="pb-2 text-right font-semibold">Uninstalled</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-150">
                    {data.byApp.map((a) => (
                      <tr key={a.appId}>
                        <td className="py-2">
                          <Link to={`/installs?appId=${a.appId}`} className="flex items-center gap-2 font-medium text-ink-800 hover:text-brand-600">
                            <AppIcon app={a} size="sm" className="!h-6 !w-6 !rounded-md !text-[9px]" />
                            <span className="truncate">{a.appName}</span>
                          </Link>
                        </td>
                        <td className="py-2 text-right tabular-nums text-ink-900">{a.active}</td>
                        <td className="py-2 text-right tabular-nums text-ink-600">{a.newInRange}</td>
                        <td className={cx("py-2 text-right tabular-nums", a.uninstalled ? "text-rose-600" : "text-ink-400")}>{a.uninstalled}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </Card>
            <Card title="Plans" subtitle={`Active installs by plan · ${data.onTrial} on a trial`}>
              <Ranked
                rows={data.byPlan.map((p) => ({
                  id: `${p.appId}:${p.planId || "none"}`,
                  name: (
                    <>
                      {p.planId ? planName(p) : "No plan"}
                      <span className="ml-1.5 font-normal text-ink-400">{p.appName}</span>
                    </>
                  ),
                  n: p.n,
                  price: priceText(p),
                  monthly: p.monthly,
                }))}
                empty="No installs yet."
                valueLabel={(r) => (
                  <>
                    {r.n} install{r.n === 1 ? "" : "s"}
                    {r.price && <span className="ml-1.5 text-ink-400">· {r.price}</span>}
                  </>
                )}
              />
              <p className="mt-4 flex items-center gap-1.5 text-[11.5px] text-ink-400">
                <Tag size={11} />
                {data.revenue?.unpricedInstalls
                  ? "Some plans have no name or price yet — add them on the app's Plans tab."
                  : "Name and price plans on each app's Plans tab."}
              </p>
            </Card>
          </div>

          <div className="grid gap-5 lg:grid-cols-2 [&>*]:min-w-0">
            <Card title="Busiest locations" subtitle={`By messages and actions · ${rangeLabel}`}>
              <Ranked
                rows={data.topLocations.map((l) => ({ id: l.locationId, name: l.name, n: l.n, failed: l.failed }))}
                empty="No activity in this range."
                valueLabel={(r) => (
                  <>
                    {fmt(r.n)}
                    {r.failed > 0 && <span className="ml-1.5 text-rose-500">{fmt(r.failed)} failed</span>}
                  </>
                )}
                to={(r) => `/installs?q=${encodeURIComponent(r.id)}`}
              />
            </Card>
            <Card title="Agencies by locations" subtitle="Active location installs per agency">
              <Ranked
                rows={data.topAgencies.map((a) => ({ id: a.companyId, name: a.name, n: a.locations }))}
                empty="No agency installs yet."
                valueLabel={(r) => `${r.n} location${r.n === 1 ? "" : "s"}`}
                to={(r) => `/locations?companyId=${encodeURIComponent(r.id)}`}
              />
            </Card>
          </div>

          <div className="grid gap-5 lg:grid-cols-2 [&>*]:min-w-0">
            <Card title="Countries" subtitle="Locations with details fetched">
              <Ranked rows={data.countries} empty="No location details yet." to={(r) => `/locations?country=${encodeURIComponent(r.name)}`} />
              <p className="mt-4 flex items-center gap-1.5 text-[11.5px] text-ink-400">
                <Globe2 size={11} /> Fills in as location details are fetched.
              </p>
            </Card>
            <Card title="Timezones" subtitle="Locations with details fetched">
              <Ranked rows={data.timezones} empty="No location details yet." />
              <p className="mt-4 flex items-center gap-1.5 text-[11.5px] text-ink-400">
                <Clock size={11} /> Useful for timing maintenance and announcements.
              </p>
            </Card>
          </div>

          <p className="flex items-center gap-1.5 text-[11.5px] text-ink-400">
            <Activity size={11} /> Activity comes from the activity log, which keeps entries for a limited time — long ranges only cover what's still kept.
          </p>
        </div>
      )}
    </div>
  );
};

export default Analytics;
